import { ListedStudy, transformListStudiesResponse } from 'helper/studies';
import {
  DownloadLinkResponse,
  ListPersonalizedParams,
  PersonalizedDownloadLinkParams,
  PersonalizedHorizon,
  PersonalizedHorizonParams,
  PersonalizedStudyParams,
  RawPersonalizedHorizonResponse,
  ResendStudyResult,
  SaveCustomStudyResponse,
  toResendStudyResult,
  transformPersonalizedMaxHorizonResponse,
} from 'helper/personalized';
import getProductKey from 'helper/requests/getProductKey';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import baseApi, { GenericResponse, rawBaseQuery } from './base';
import { CancelStudyResponse } from './studies';

export const personalizedApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listPersonalizedStudies: build.query<ListedStudy[], ListPersonalizedParams>({
      query: ({ ampereCases, page, pageSize }) => ({
        url: 'produtos/previvaz-personalizado/get-list',
        method: 'POST',
        body: {
          casos_ampere: ampereCases,
          page,
          page_size: pageSize,
        },
      }),
      transformResponse: transformListStudiesResponse,
      providesTags: ['PersonalizedStudy'],
    }),
    createPersonalizedRequest: build.mutation<SaveCustomStudyResponse, PersonalizedStudyParams>({
      query: (params) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-personalizado/save/?product_key=${productKey}`,
          method: 'POST',
          body: params,
        };
      },
    }),
    personalizedExecutionQueue: build.mutation<SaveCustomStudyResponse, number>({
      query: (studyID) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-personalizado/send-data/?product_key=${productKey}&estudo=${studyID}`,
          method: 'POST',
        };
      },
      // O estudo enviado entra na lista: sem isto, Meus Estudos mostrava o cache antigo (#1987)
      invalidatesTags: ['PersonalizedStudy'],
    }),
    personalizedDownloadLink: build.mutation<DownloadLinkResponse, PersonalizedDownloadLinkParams>({
      query: (params) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-personalizado/get-download-link/?product_key=${productKey}`,
          method: 'POST',
          body: params,
        };
      },
    }),
    deletePersonalizedStudy: build.mutation<GenericResponse<null>, number>({
      query: (id) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-personalizado/delete/?product_key=${productKey}`,
          method: 'DELETE',
          body: { id },
        };
      },
      invalidatesTags: ['PersonalizedStudy'],
    }),
    // Mesma rota do Upload (o backend cancela qualquer estudo); muda só a lista invalidada
    cancelPersonalizedStudy: build.mutation<CancelStudyResponse, number>({
      query: (processId) => ({
        url: 'produtos/flux/kill-process/',
        method: 'POST',
        body: { process_id: processId },
      }),
      invalidatesTags: ['PersonalizedStudy'],
    }),
    // Reenviar da tela nova (#1994): lê o `success` do corpo, que vem sem `code`. O `reexecute`
    // legado (Upload, Row antiga e mobile) continua como está. Sem `product_key`, como o legado:
    // a rota re-execute só exige login (wsgi-beta `controller/flux.py`).
    resendPersonalizedStudy: build.mutation<ResendStudyResult, number>({
      queryFn: async (processId, api, extraOptions) => toResendStudyResult(await rawBaseQuery(
        { url: 'produtos/flux/re-execute', method: 'POST', body: { process_id: processId } },
        api,
        extraOptions,
      )),
      invalidatesTags: ['PersonalizedStudy'],
    }),
    personalizedHorizon: build.query<PersonalizedHorizon, PersonalizedHorizonParams>({
      queryFn: async (params, api, extraOptions) => {
        const productKey = getProductKey('prevs-personalizado');

        const response = await rawBaseQuery(
          {
            url: `produtos/previvaz-personalizado/horizonte-max/?product_key=${productKey}`,
            method: 'POST',
            body: {
              data_previsao: params.forecastDate,
              lista_modelos: [params.model],
              runtime: params.runtime,
              membro: params.member,
              nivel_atm: 'single_level',
              var: 'prec',
            },
          },
          api,
          extraOptions,
        ) as QueryReturnValue<RawPersonalizedHorizonResponse, unknown, unknown>;

        const transformedResponse = {
          ...response,
          ...transformPersonalizedMaxHorizonResponse(response.data),
        } as QueryReturnValue<PersonalizedHorizon, unknown, unknown>;

        return transformedResponse;
      },
    }),
  }),
});

export const {
  useListPersonalizedStudiesQuery,
  useCreatePersonalizedRequestMutation,
  usePersonalizedExecutionQueueMutation,
  usePersonalizedDownloadLinkMutation,
  useLazyPersonalizedHorizonQuery,
  useDeletePersonalizedStudyMutation,
  useCancelPersonalizedStudyMutation,
  useResendPersonalizedStudyMutation,
} = personalizedApi;
